import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown } from "lucide-react";
import { YearSelector } from '@/components/common/YearSelector';
import { Transaction, Conta } from "@/types/database";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getEffectiveTransactionDate } from '@/lib/utils';
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { CustomTooltipProps, TooltipPayload } from "@/types/recharts";

interface AnnualBalanceReportProps {
  transactions: Transaction[];
  selectedDate?: Date;
  onDateChange?: (date: Date) => void;
  onNavigateToTransactions?: (month: number, year: number) => void;
}

export function AnnualBalanceReport({ transactions, selectedDate, onDateChange, onNavigateToTransactions }: AnnualBalanceReportProps) {
  type BarClickData = { payload?: { month?: string } };
  const [currentYear, setCurrentYear] = useState(() => {
    if (selectedDate) {
      return selectedDate.getFullYear();
    }
    return new Date().getFullYear();
  });

  // Buscar contas para calcular datas efetivas
  const { data: accounts = [] } = useQuery({
    queryKey: ['contas'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contas')
        .select('*')
        .eq('ativo', true);
      
      if (error) throw error;
      return data || [];
    }
  });

  // Update local state when selectedDate prop changes
  useEffect(() => {
    if (selectedDate) {
      setCurrentYear(selectedDate.getFullYear());
    }
  }, [selectedDate]);

  const yearlyData = useMemo(() => {
    const months = [];
    
    // Generate 12 months of data for the selected year
    for (let month = 0; month < 12; month++) {
      const monthStart = startOfMonth(new Date(currentYear, month, 1));
      const monthEnd = endOfMonth(monthStart);
      
      const monthTransactions = transactions.filter(t => {
        // Usar data efetiva (considerando cartão de crédito)
        const transactionDate = getEffectiveTransactionDate(t, accounts as Conta[]);
        
        return transactionDate && 
               transactionDate instanceof Date && 
               !isNaN(transactionDate.getTime()) && 
               transactionDate >= monthStart && 
               transactionDate <= monthEnd && 
               !t.ignorar_transacao;
      });
      
      const income = monthTransactions
        .filter(t => t.tipo === 'entrada' && t.foi_paga)
        .reduce((sum, t) => sum + Number(t.valor), 0);
      
      const expenses = monthTransactions
        .filter(t => t.tipo === 'saida')
        .reduce((sum, t) => sum + Number(t.valor), 0);
      
      months.push({
        month: format(monthStart, 'MMM', { locale: ptBR }),
        fullMonth: format(monthStart, 'MMMM yyyy', { locale: ptBR }),
        income,
        expenses,
        balance: income - expenses,
        date: monthStart
      });
    }
    
    return months;
  }, [transactions, currentYear, accounts]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const handleYearChange = (year: number) => {
    setCurrentYear(year);
    const newDate = new Date(year, 0, 1); // January 1st of the selected year
    onDateChange?.(newDate);
  };

  const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium mb-2">{label}</p>
          {payload.map((entry: TooltipPayload, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.dataKey === 'income' ? 'Receitas' : 
               entry.dataKey === 'expenses' ? 'Despesas' : 
               entry.dataKey === 'balance' ? 'Saldo' : entry.dataKey}: {formatCurrency(entry.value as number)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Calculate year totals
  const yearTotals = useMemo(() => {
    const totalIncome = yearlyData.reduce((sum, month) => sum + month.income, 0);
    const totalExpenses = yearlyData.reduce((sum, month) => sum + month.expenses, 0);
    const balance = totalIncome - totalExpenses;
    
    return {
      totalIncome,
      totalExpenses,
      balance
    };
  }, [yearlyData]);

  return (
    <div className="space-y-6">
      {/* Year Balance Summary Card */}
      <Card className="bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base font-medium text-muted-foreground">
            Balanço Anual
          </CardTitle>
          <div className="p-2 rounded-md bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary">
            <TrendingUp className="h-4 w-4" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-base font-bold text-primary dark:text-primary">
            {formatCurrency(yearTotals.balance)}
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {currentYear}
          </p>
        </CardContent>
      </Card>

      {/* Year Navigation */}
      <YearSelector
        selectedYear={currentYear}
        onYearChange={handleYearChange}
      />

      {/* Chart and Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Receitas vs Despesas - {currentYear}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={yearlyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="month" 
                    className="text-muted-foreground"
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    className="text-muted-foreground"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      if (value >= 1000) {
                        return `${(value / 1000).toFixed(0)}k`;
                      }
                      return value.toString();
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="income" 
                    fill="hsl(142, 76%, 36%)" 
                    name="Receitas"
                    radius={[2, 2, 0, 0]}
                    onClick={(d: BarClickData) => {
                      const label = String(d?.payload?.month || '').toLowerCase();
                      const map: Record<string, number> = { jan: 0, fev: 1, mar: 2, abr: 3, mai: 4, jun: 5, jul: 6, ago: 7, set: 8, out: 9, nov: 10, dez: 11 };
                      const m = map[label];
                      const y = currentYear;
                      if (typeof m === 'number') onNavigateToTransactions?.(m, y);
                    }}
                  />
                  <Bar 
                    dataKey="expenses" 
                    fill="hsl(0, 84%, 60%)" 
                    name="Despesas"
                    radius={[2, 2, 0, 0]}
                    onClick={(d: BarClickData) => {
                      const label = String(d?.payload?.month || '').toLowerCase();
                      const map: Record<string, number> = { jan: 0, fev: 1, mar: 2, abr: 3, mai: 4, jun: 5, jul: 6, ago: 7, set: 8, out: 9, nov: 10, dez: 11 };
                      const m = map[label];
                      const y = currentYear;
                      if (typeof m === 'number') onNavigateToTransactions?.(m, y);
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Details List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Detalhes Mensais</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {yearlyData.map((month, index) => (
              <div 
                key={index} 
                className="border-b border-border last:border-b-0 pb-4 last:pb-0 cursor-pointer hover:bg-accent/30 rounded"
                onClick={() => onNavigateToTransactions?.(month.date.getMonth(), currentYear)}
              >
                <h4 className="font-medium text-base mb-2 capitalize">
                  {month.month} {currentYear}
                </h4>
                <div className="space-y-1 text-base">
                  <div className="flex justify-between items-center">
                    <span className="text-green-600">Receitas:</span>
                    <span className="font-medium text-green-600">
                      {formatCurrency(month.income)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-red-600">Despesas:</span>
                    <span className="font-medium text-red-600">
                      {formatCurrency(month.expenses)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-1 border-t border-muted">
                    <span className="font-medium">Saldo:</span>
                    <span className="font-medium text-primary dark:text-primary">
                    {formatCurrency(month.balance)}
                  </span>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
